class PackOffer extends HTMLElement {
  connectedCallback() {
    this.tiers  = this.json('tiers').sort((a, b) => a.pens - b.pens);
    this.plans  = this.json('plans');
    this.scents = this.json('scents');
    this.gifts  = this.json('gifts').sort((a, b) => a.threshold - b.threshold);
    if (!this.tiers.length || !this.scents.length) return;

    this.fmt = this.dataset.moneyFormat || '${{amount}}';
    this.picks = {};
    this.controller = null;

    const wanted = parseInt(this.dataset.defaultPack, 10);
    this.tier = this.tiers.find((t) => t.pens === wanted) || this.tiers[0];
    this.planId = this.plans.length ? this.plans[0].id : null;
    this.subscribe = this.dataset.subscribeDefault === 'true' && !!this.planId;

    this.$scents    = this.querySelector('[data-po-scents]');
    this.$tiers     = this.querySelector('[data-po-tiers]');
    this.$remaining = this.querySelector('[data-po-remaining]');
    this.$sub       = this.querySelector('[data-po-sub]');
    this.$subInput  = this.querySelector('[data-po-subscribe]');
    this.$subPrice  = this.querySelector('[data-po-sub-price]');
    this.$freqWrap  = this.querySelector('[data-po-freq-wrap]');
    this.$freq      = this.querySelector('[data-po-freq]');
    this.$gifts     = this.querySelector('[data-po-gifts]');
    this.$giftGrid  = this.querySelector('[data-po-gift-grid]');
    this.$cta       = this.querySelector('[data-po-add]');
    this.$error     = this.querySelector('[data-po-error]');
    this.$reassure  = this.querySelector('.po__reassure');
    this.reassureBase = this.$reassure ? this.$reassure.textContent.trim() : '';

    this.buildScents();
    this.buildTiers();
    this.buildFrequency();

    this.$subInput.addEventListener('change', () => {
      this.subscribe = this.$subInput.checked;
      this.render();
    });
    if (this.$freq) {
      this.$freq.addEventListener('change', (e) => {
        this.planId = Number(e.target.value);
        this.render();
      });
    }
    this.$cta.addEventListener('click', () => this.addToCart());

    this.render();
  }

  json(key) {
    try { return JSON.parse(this.dataset[key] || '[]'); } catch (e) { return []; }
  }

  money(cents) {
    const n = (Math.round(cents) / 100).toFixed(2);
    return this.fmt.indexOf('{{amount}}') > -1 ? this.fmt.replace('{{amount}}', n) : '$' + n;
  }

  get total() {
    return Object.values(this.picks).reduce((a, b) => a + b, 0);
  }

  // Price for a tier under the current subscribe/plan choice.
  priceOf(tier) {
    if (this.subscribe && this.planId && tier.plans && tier.plans[this.planId] != null) {
      return tier.plans[this.planId];
    }
    return tier.price;
  }

  /* ---------------- build ---------------- */

  buildScents() {
    const icon = (d) =>
      `<svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="${d}" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`;
    const plus = icon('M6 1.5V10.5M1.5 6H10.5');
    const minus = icon('M1.5 6H10.5');

    this.scents.forEach((s) => {
      this.picks[s.name] = 0;

      const row = document.createElement('div');
      row.className = 'po__scent';
      row.dataset.scent = s.name;
      row.dataset.chosen = '0';

      // Split "Rosa Damascena - Hydration & Soothing" into name + descriptor.
      const parts = s.name.split(' - ');
      const main = parts[0];
      const sub = parts.slice(1).join(' - ');

      row.innerHTML =
        (s.img ? `<img class="po__scent-thumb" src="${s.img}" alt="" loading="lazy" width="44" height="44">`
               : `<span class="po__scent-thumb"></span>`) +
        `<div class="po__scent-name">
           <span class="po__scent-main">${this.esc(main)}${sub ? `<span class="po__scent-sub">${this.esc(sub)}</span>` : ''}</span>
           ${s.badge ? `<span class="po__badge">${this.esc(s.badge)}</span>` : ''}
         </div>
         <div class="po__step">
           <button type="button" class="po__step-btn po__step-btn--minus" data-dec hidden aria-label="Remove one ${this.esc(main)}">${minus}</button>
           <span class="po__step-qty" data-qty hidden>0</span>
           <button type="button" class="po__step-btn po__step-btn--add" data-inc aria-label="Add one ${this.esc(main)}">${plus}</button>
         </div>`;

      row.querySelector('[data-inc]').addEventListener('click', () => this.bump(s.name, 1));
      row.querySelector('[data-dec]').addEventListener('click', () => this.bump(s.name, -1));
      this.$scents.appendChild(row);
    });
  }

  buildTiers() {
    this.tiers.forEach((tier) => {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'po__tier';
      el.setAttribute('role', 'radio');
      el.dataset.pens = tier.pens;
      if (!tier.available) el.disabled = true;
      el.addEventListener('click', () => this.selectTier(tier));
      this.$tiers.appendChild(el);
    });
  }

  buildFrequency() {
    if (!this.$freq || this.plans.length === 0) {
      if (this.$freqWrap) this.$freqWrap.hidden = true;
      return;
    }
    if (this.plans.length === 1) { this.$freqWrap.hidden = true; return; }

    this.plans.forEach((p) => {
      const o = document.createElement('option');
      o.value = p.id;
      o.textContent = this.freqLabel(p.name);
      this.$freq.appendChild(o);
    });
    this.$freq.value = this.planId;
  }

  // "3 month subscription with 15% discount" -> "Delivered every 3 months"
  freqLabel(name) {
    const m = String(name).match(/(\d+)\s*(day|week|month)/i);
    if (!m) return name;
    const n = Number(m[1]);
    const unit = m[2].toLowerCase();
    return n === 1 ? `Delivered every ${unit}` : `Delivered every ${n} ${unit}s`;
  }

  /* ---------------- interaction ---------------- */

  bump(name, delta) {
    const next = (this.picks[name] || 0) + delta;
    if (next < 0) return;
    if (delta > 0 && this.total >= this.tier.pens) return;
    this.picks[name] = next;
    this.render();
  }

  selectTier(tier) {
    this.tier = tier;
    // Trim the selection down if the new pack is smaller than what was chosen.
    let over = this.total - tier.pens;
    if (over > 0) {
      for (const name of Object.keys(this.picks).slice().reverse()) {
        if (over <= 0) break;
        const take = Math.min(this.picks[name], over);
        this.picks[name] -= take;
        over -= take;
      }
    }
    this.render();
  }

  /* ---------------- render ---------------- */

  render() {
    const budget = this.tier.pens;
    const total = this.total;
    const left = budget - total;
    const complete = left === 0;

    // scent rows
    this.$scents.querySelectorAll('.po__scent').forEach((row) => {
      const qty = this.picks[row.dataset.scent] || 0;
      row.dataset.chosen = String(qty);
      const dec = row.querySelector('[data-dec]');
      const qtyEl = row.querySelector('[data-qty]');
      const inc = row.querySelector('[data-inc]');
      dec.hidden = qty === 0;
      qtyEl.hidden = qty === 0;
      qtyEl.textContent = qty;
      inc.disabled = total >= budget;
    });

    // remaining counter
    this.$remaining.dataset.complete = String(complete);
    this.$remaining.textContent = complete
      ? `${budget} of ${budget} selected`
      : `${total} of ${budget} selected`;

    // tiers
    this.$tiers.querySelectorAll('.po__tier').forEach((el) => {
      const tier = this.tiers.find((t) => t.pens === Number(el.dataset.pens));
      const on = tier.pens === this.tier.pens;
      const now = this.priceOf(tier);
      const save = tier.compare > now ? Math.round((1 - now / tier.compare) * 100) : 0;
      el.setAttribute('aria-checked', String(on));
      el.innerHTML =
        (save > 0 ? `<span class="po__tier-save">Save ${save}%</span>` : '') +
        `<span class="po__tier-name">${this.esc(tier.title)}</span>` +
        `<span class="po__tier-unit">${this.money(now / tier.pens)} / pen</span>`;
    });

    // subscribe panel
    const subPrice = this.tier.plans && this.planId ? this.tier.plans[this.planId] : null;
    this.$sub.dataset.open = String(this.subscribe);
    if (subPrice != null) {
      this.$subPrice.innerHTML = this.subscribe
        ? `<s>${this.money(this.tier.price)}</s><b>${this.money(subPrice)}</b>`
        : `<b>${this.money(this.tier.price)}</b>`;
    } else {
      this.$subPrice.textContent = this.money(this.tier.price);
    }

    // gifts
    if (this.gifts.length) {
      this.$gifts.hidden = false;
      this.$giftGrid.innerHTML = this.gifts.map((g) => {
        const open = budget >= g.threshold;
        return `<div class="po__gift" data-unlocked="${open}">
          <span class="po__gift-tag">${open ? 'FREE' : `${g.threshold}+ PENS`}</span>
          ${g.img ? `<img src="${g.img}" alt="" loading="lazy">` : ''}
          <span class="po__gift-name">${this.esc(g.title)}</span>
          ${g.value ? `<span class="po__gift-val">${this.esc(g.value)}</span>` : ''}
        </div>`;
      }).join('');
    }

    // cta
    this.$cta.disabled = !complete;
    this.$cta.textContent = complete
      ? `Add ${this.tier.title} for ${this.money(this.priceOf(this.tier))}`
      : left === budget
        ? `Choose your ${budget === 1 ? 'pen' : 'pens'}`
        : `Choose ${left} more ${left === 1 ? 'pen' : 'pens'}`;

    // The guarantee card below carries the money-back promise, so this line
    // only states what's true of a subscription — and hides for one-time.
    if (this.$reassure) {
      this.$reassure.hidden = !this.subscribe;
      if (this.subscribe) this.$reassure.textContent = this.reassureBase;
    }

    this.hideError();
  }

  esc(s) { const d = document.createElement('div'); d.textContent = s == null ? '' : s; return d.innerHTML; }

  /* ---------------- cart ---------------- */

  buildItems() {
    // Expand {Rosa: 2, Jasmine: 1} into Pen 1, Pen 2, Pen 3.
    const properties = { _po_pack: String(this.tier.pens) };
    let n = 0;
    this.scents.forEach((s) => {
      for (let i = 0; i < (this.picks[s.name] || 0); i++) {
        n += 1;
        properties['Pen ' + n] = s.name;
      }
    });

    // Quantity is ALWAYS 1 — the variant price already contains the pack
    // discount, so quantity > 1 would multiply the whole pack.
    const main = { id: this.tier.id, quantity: 1, properties: properties };
    if (this.subscribe && this.planId) main.selling_plan = this.planId;

    const items = [main];

    // Gifts never carry the selling plan, or a free gift ships on every renewal.
    this.gifts
      .filter((g) => g.id && this.tier.pens >= g.threshold)
      .forEach((g) => items.push({
        id: g.id, quantity: 1,
        properties: { _po_gift: '1', _po_pack_tier: String(this.tier.pens) },
      }));

    return items;
  }

  async ownLineKeys(root) {
    const res = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) return [];
    const cart = await res.json();
    return (cart.items || [])
      .filter((i) => { const p = i.properties || {}; return p._po_pack || p._po_gift; })
      .map((i) => i.key);
  }

  async removeLines(root, keys) {
    if (!keys || !keys.length) return;
    const updates = {};
    keys.forEach((k) => { updates[k] = 0; });
    await fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    });
  }

  async addToCart() {
    this.hideError();
    this.$cta.setAttribute('aria-busy', 'true');
    this.$cta.disabled = true;

    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    try {
      // Collected BEFORE the add, removed only AFTER it succeeds — clearing
      // first would empty the cart if the add then failed.
      const stale = await this.ownLineKeys(root);

      const res = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: this.buildItems() }),
        signal: this.controller.signal,
      });
      const data = await res.json();
      if (!res.ok) { this.showError(data.description || data.message); return; }

      await this.removeLines(root, stale);

      document.dispatchEvent(new CustomEvent('pack-offer:added', { detail: data }));

      if (this.dataset.cartBehaviour === 'redirect') { window.location.href = root + 'cart'; return; }

      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function') {
        const r = await fetch(root + '?sections=cart-drawer,cart-icon-bubble');
        const sections = await r.json();
        drawer.renderContents({ sections: sections, sections_html: sections });
      } else {
        window.location.href = root + 'cart';
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      this.showError();
    } finally {
      this.$cta.removeAttribute('aria-busy');
      this.render();
    }
  }

  showError(msg) {
    if (!this.$error) return;
    this.$error.textContent = msg || 'Something went wrong. Please try again.';
    this.$error.hidden = false;
  }
  hideError() {
    if (!this.$error) return;
    this.$error.hidden = true;
    this.$error.textContent = '';
  }
}

if (!customElements.get('pack-offer')) customElements.define('pack-offer', PackOffer);
